import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;

/** The house's side walls (m): their half width near us, at the proscenium, and the proscenium's distance. */
const HALL = { a: 11, aStage: 8.6, zNear: 0.9, zStage: 24 };
/** The stage's opening in the proscenium (m): its half width, its floor and its top. */
const STAGE = { half: 7, floor: 1.2, top: 12.5, deep: 16 };
/** The floors of the tiers (m above the stalls): the parterre boxes, three tiers of boxes, the gallery. */
const TIERS = [ 2.6, 6.2, 9.8, 13.4, 17.0 ];
/** The ceiling's rim (m). */
const CEIL = 20.6;
/** The spotlight rigs on the boxes' tops at the proscenium's upper corners (m), and their lamps' offsets. */
// (kaizen 1) standing on the third tier's parapet at the last box, not hanging in the air
const RIG = { x: 7.6, y: 14.55, z: 22.4 };
const RIG_LAMPS = [ 6.9, 7.35, 7.8, 8.25 ];
/** A parapet's height (m). */
const PARAPET = 1.0;

/** A spotlight (owner: lit on the charge): where it hangs and the point on the stage it aims at, in metres. */
interface Spot {
  sx: number;
  sy: number;
  sz: number;
  tx: number;
  tz: number;
  phase: number;
  sweep: number;
}

/** A box lamp on a parapet, in page pixels. */
interface Lamp {
  x: number;
  y: number;
  r: number;
  phase: number;
}

/**
 * Vienna's hero board (2026-10-18), after the owner's photograph of the Staatsoper's house,
 * turned round (owner) to look from the stalls at the stage, in true perspective (everything in metres): the
 * side walls' tiers of cream and gold parapets over the boxes deep red behind their curtains, running to the
 * proscenium's gilded frame; the house curtain drawn aside to left and right under its swagged pelmet; the
 * dark stage; the pit's rail and the rows of red velvet seats with the carpet down the aisle. The house is
 * dark and dim at rest, its parapet lamps glowing low; on the charge the spotlights come on and sweep the
 * stage. The page's wallpaper is soft lights. Sized by the stage's height.
 */
@Component( {
  selector: 'app-hero-vienna',
  template: '<canvas #canvas class="hvie-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.hvie-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroViennaComponent extends HeroMotifBase {

  /** The house: the tiers, the stalls and the seats, painted once. */
  private back: HTMLCanvasElement | null = null;
  /** The same house with its lights up, faded in on the charge. */
  private backLit: HTMLCanvasElement | null = null;
  private spots: Spot[] = [];
  private lamps: Lamp[] = [];
  private lampSprite: HTMLCanvasElement | null = null;
  private spotLayer: HTMLCanvasElement | null = null;
  /** A spot's cone, painted once per palette: a soft gaussian across, brightest near its lamp. */
  private beamSprite: HTMLCanvasElement | null = null;
  private scaleK = 1;
  private eye = 2.4;
  private F = 0;
  private hz = 0;
  private vx = 0;

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.2;
  }

  protected colorsChanged(): void {
    this.back = null;
    this.lampSprite = null;
    this.beamSprite = null;
  }

  /** Project a point: x across (metres from the aisle), y up from the stalls' floor, z away from the stage. */
  private P( x: number, y: number, z: number ): [ number, number ] {
    return [ this.vx + this.F * x / z, this.hz - this.F * ( y - this.eye ) / z ];
  }

  protected build(): void {
    const W = this.width, H = this.height;
    this.scaleK = Math.max( 0.55, Math.min( 1.25, H / 760 ) );
    this.F = 0.95 * H;
    this.hz = 0.6 * H;
    this.vx = W / 2;
    this.back = null;
    const r = this.seededLocal( 31 );
    // (owner's photograph) the spots in the two rigs on the boxes' tops at the proscenium's upper corners,
    // each aimed at its own point on the stage and sweeping slowly about it
    const aims: [ number, number, number, number, number ][] = [];
    for ( const side of [ -1, 1 ] ) {
      RIG_LAMPS.forEach( ( lx, q ) => aims.push( [ side * lx, RIG.y + 0.42, RIG.z, -side * ( 4.2 - q * 1.8 ), 30 + ( q % 2 ) * 4 ] ) );
    }
    this.spots = aims.map( ( [ sx, sy, sz, tx, tz ] ) => ( { sx, sy, sz, tx, tz, phase: r() * Math.PI * 2, sweep: 0.8 + r() * 0.8 } ) );
  }

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width, H = this.height;
    if ( this.frozen ) {
      // (owner) the page's wallpaper is lights
      this.drawWallLights( ctx );
      return;
    }
    if ( !this.back ) { this.bake( ctx.getTransform().a, true ); this.bake( ctx.getTransform().a ); }
    if ( !this.back ) { return; }
    ctx.drawImage( this.back, 0, 0, W, H );
    // (owner: everything a little brighter on the charge) the lit house fading in over the dark one
    const e = this.energy, lift = e * e * ( 3 - 2 * e );
    if ( this.backLit && lift > 0.01 ) {
      ctx.globalAlpha = lift * 0.85;
      ctx.drawImage( this.backLit, 0, 0, W, H );
      ctx.globalAlpha = 1;
    }
    this.drawLamps( ctx );
    this.drawSpots( ctx );
  }

  /** The parapets' small lamps: a soft warm halo each, brighter on the charge, each breathing on its own. */
  private drawLamps( ctx: CanvasRenderingContext2D ): void {
    if ( !this.lampSprite ) {
      const size = 64, cv = document.createElement( 'canvas' );
      cv.width = cv.height = size;
      const lg = cv.getContext( '2d' )!, gr = lg.createRadialGradient( size / 2, size / 2, 0, size / 2, size / 2, size / 2 );
      gr.addColorStop( 0, 'rgba(255,236,190,0.9)' );
      gr.addColorStop( 0.18, 'rgba(255,214,140,0.45)' );
      gr.addColorStop( 0.55, 'rgba(240,170,90,0.12)' );
      gr.addColorStop( 1, 'rgba(240,160,80,0)' );
      lg.fillStyle = gr;
      lg.fillRect( 0, 0, size, size );
      this.lampSprite = cv;
    }
    const e = this.energy, t = this.time;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for ( const l of this.lamps ) {
      const breathe = 0.85 + 0.15 * Math.sin( t * W0 * 3 + l.phase );
      // (owner: the side lights shine brighter on the charge)
      // (owner: darker once the charge is over)
      ctx.globalAlpha = Math.min( 1, ( 0.16 + 0.84 * e ) * breathe );
      const rr = l.r * ( 1 + 0.9 * e );
      ctx.drawImage( this.lampSprite, l.x - rr, l.y - rr, rr * 2, rr * 2 );
    }
    ctx.restore();
  }

  /**
   * (owner: on the charge the spotlights come on) each beam a soft cone from its lamp to its pool on the
   * boards, the pool sweeping slowly; a warm spill over the stage and the curtains; nothing at rest
   */
  private drawSpots( out: CanvasRenderingContext2D ): void {
    const e = this.energy, a = e * e * ( 3 - 2 * e );
    if ( a < 0.01 ) { return; }
    // (kaizen 9) drawn on a layer of their own, eased off behind the hero's words, then added to the house
    const sc = out.getTransform().a, cw = Math.ceil( this.width * sc ), ch = Math.ceil( this.height * sc );
    if ( !this.spotLayer || this.spotLayer.width !== cw || this.spotLayer.height !== ch ) {
      this.spotLayer = document.createElement( 'canvas' );
      this.spotLayer.width = cw;
      this.spotLayer.height = ch;
    }
    const ctx = this.spotLayer.getContext( '2d' )!;
    ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    ctx.clearRect( 0, 0, cw, ch );
    ctx.setTransform( sc, 0, 0, sc, 0, 0 );
    const t = this.time, F = this.F, fl = STAGE.floor;
    const [ , , gold, pale ] = this.five();
    const warm = this.mix( [ 255, 246, 226 ], pale, 0.25 );
    ctx.save();
    // (owner: what lies over what) the light stays above the pit's rail: the seats are in front of the stage
    const [ , railY ] = this.P( 0, 1.18, HALL.zStage - 3.1 );
    ctx.globalCompositeOperation = 'lighter';
    // the spill: the stage and the curtains warmed by the light
    const [ cx, cy ] = this.P( 0, fl + 3, HALL.zStage + 4 ), rs = F * 9 / HALL.zStage;
    const sp = ctx.createRadialGradient( cx, cy, 0, cx, cy, rs );
    sp.addColorStop( 0, this.rgba( this.mix( warm, gold, 0.3 ), 0.22 * a ) );
    sp.addColorStop( 1, this.rgba( gold, 0 ) );
    ctx.fillStyle = sp;
    ctx.fillRect( cx - rs, cy - rs, rs * 2, rs * 2 );
    // (kaizen) a stage haze the beams light, drifting slowly across the opening
    {
      const [ hx0, hy0 ] = this.P( -STAGE.half, STAGE.top, HALL.zStage ), [ hx1, hy1 ] = this.P( STAGE.half, fl, HALL.zStage );
      for ( let q = 0; q < 5; q++ ) {
        const u = ( ( q / 5 + t / LOOP ) % 1 + 1 ) % 1, hx = hx0 + ( hx1 - hx0 ) * u, hy = hy0 + ( hy1 - hy0 ) * ( 0.45 + 0.35 * Math.sin( q * 2.1 + t * W0 ) );
        const hr = ( hx1 - hx0 ) * 0.4;
        const hz2 = ctx.createRadialGradient( hx, hy, 0, hx, hy, hr );
        hz2.addColorStop( 0, this.rgba( warm, 0.05 * a * Math.sin( u * Math.PI ) ) );
        hz2.addColorStop( 1, this.rgba( warm, 0 ) );
        ctx.fillStyle = hz2;
        ctx.fillRect( hx - hr, hy - hr, hr * 2, hr * 2 );
      }
    }
    for ( const s of this.spots ) {
      const tx = s.tx + Math.sin( t * W0 * 2 + s.phase ) * s.sweep, tz = s.tz + Math.cos( t * W0 + s.phase ) * 0.8;
      const [ ax, ay ] = this.P( s.sx, s.sy, s.sz ), [ bx, by ] = this.P( tx, fl, tz );
      const R = 1.5, rx = F * R / tz, [ , ny ] = this.P( tx, fl, tz - R ), [ , fy ] = this.P( tx, fl, tz + R ), ry = Math.abs( ny - fy ) / 2;
      const dx = bx - ax, dy = by - ay, dl = Math.hypot( dx, dy ) || 1, px = -dy / dl, py = dx / dl;
      const w0 = F * 0.12 / s.sz;
      // a soft wide cone, then its brighter core
      // (owner: the beams' quality, as on Paris's searchlights) one soft gaussian cone baked once, laid from
      // the lamp to its pool, no straight edge anywhere
      if ( !this.beamSprite ) { this.beamSprite = this.makeBeam( warm ); }
      // (owner: the beam ends flat on the floor) the cone runs on past its pool and is cut by the boards: it
      // shows only above the pool's line and within the pool's own ellipse on the floor
      ctx.save();
      ctx.beginPath();
      ctx.rect( 0, 0, this.width, by );
      ctx.ellipse( bx, by, rx * 0.95, Math.max( 1, ry ) * 0.95, 0, 0, Math.PI * 2 );
      ctx.clip();
      ctx.globalAlpha = Math.min( 1, 0.55 * a );
      ctx.translate( ax, ay );
      ctx.rotate( Math.atan2( dy, dx ) );
      ctx.drawImage( this.beamSprite, 0, -rx * 1.25, dl * 1.25, rx * 2.5 );
      ctx.restore();
      // (kaizen 6) motes of dust drifting slowly down the beam, catching its light
      for ( let q = 0; q < 14; q++ ) {
        const u = ( ( q * 0.618 + t / LOOP * ( 1 + ( q % 3 ) ) + s.phase ) % 1 + 1 ) % 1;
        const v = Math.sin( q * 12.9898 + s.phase * 3 ) * 0.9, w = w0 + ( rx - w0 ) * u;
        const mx = ax + dx * u + px * w * v * 0.8, my = ay + dy * u + py * w * v * 0.3;
        const tw = 0.5 + 0.5 * Math.sin( t * W0 * 5 + q * 2.3 );
        ctx.fillStyle = this.rgba( warm, 0.5 * a * tw * Math.min( 1, u * 4 ) );
        ctx.beginPath(); ctx.arc( mx, my, Math.max( 0.6, F * 0.02 / s.sz * ( 1 + u ) ), 0, Math.PI * 2 ); ctx.fill();
      }
      // the pool on the boards
      const pool = ctx.createRadialGradient( bx, by, 0, bx, by, rx );
      pool.addColorStop( 0, this.rgba( warm, 0.24 * a ) );
      pool.addColorStop( 0.45, this.rgba( warm, 0.12 * a ) );
      pool.addColorStop( 1, this.rgba( warm, 0 ) );
      ctx.fillStyle = pool;
      ctx.beginPath(); ctx.ellipse( bx, by, rx, Math.max( 1, ry ), 0, 0, Math.PI * 2 ); ctx.fill();
      // the lamp itself where it shows (the side boxes'), a bright heart
      if ( ay > 0 ) {
        const hr = F * 0.5 / s.sz;
        const lamp = ctx.createRadialGradient( ax, ay, 0, ax, ay, hr );
        lamp.addColorStop( 0, this.rgba( [ 255, 252, 240 ], 0.9 * a ) );
        lamp.addColorStop( 1, this.rgba( warm, 0 ) );
        ctx.fillStyle = lamp;
        ctx.fillRect( ax - hr, ay - hr, hr * 2, hr * 2 );
      }
    }
    ctx.restore();
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    // the light fading out softly at the pit's rail, never a hard line: the seats stand in front
    const rf = ctx.createLinearGradient( 0, railY - 16, 0, railY + 4 );
    rf.addColorStop( 0, 'rgba(0,0,0,0)' );
    rf.addColorStop( 1, 'rgba(0,0,0,1)' );
    ctx.fillStyle = rf;
    ctx.fillRect( 0, railY - 16, this.width, this.height - railY + 16 );
    for ( const b of this.blocks ) {
      const cx = b.x + b.w / 2, cy = b.y + b.h / 2, rw = b.w * 0.5 + 16, rh = b.h * 0.6 + 14;
      const er = ctx.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      er.addColorStop( 0, 'rgba(0,0,0,0.8)' );
      er.addColorStop( 0.6, 'rgba(0,0,0,0.5)' );
      er.addColorStop( 1, 'rgba(0,0,0,0)' );
      ctx.setTransform( sc * rw, 0, 0, sc * rh, sc * cx, sc * cy );
      ctx.fillStyle = er;
      ctx.fillRect( -1, -1, 2, 2 );
    }
    ctx.restore();
    out.save();
    out.globalCompositeOperation = 'lighter';
    out.drawImage( this.spotLayer, 0, 0, this.width, this.height );
    out.restore();
  }

  /** A spot's cone pointing right from its lamp at the left middle (Paris's searchlight sprite). */
  private makeBeam( col: RGB ): HTMLCanvasElement {
    const w = 512, h = 128;
    const cv = document.createElement( 'canvas' );
    cv.width = w; cv.height = h;
    const c = cv.getContext( '2d' )!;
    const img = c.createImageData( w, h );
    for ( let x = 0; x < w; x++ ) {
      const t = x / w;
      const halfW = ( 0.04 + 0.96 * t ) * h / 2;
      // (owner: the beams' lower ends neat) the cone dies away softly into its pool instead of ending square
      const tail = t > 0.9 ? Math.pow( Math.cos( ( t - 0.9 ) / 0.1 * Math.PI / 2 ), 1.4 ) : 1;
      const along = Math.min( 1, t * 30 ) * ( 1 - 0.45 * t ) * tail;
      for ( let y = 0; y < h; y++ ) {
        const d = ( y - h / 2 + 0.5 ) / halfW;
        const al = along * ( 0.55 * Math.exp( -d * d * 2.4 ) + 0.4 * Math.exp( -d * d * 12 ) * ( 1 - t * 0.6 ) );
        const i = ( y * w + x ) * 4;
        img.data[ i ] = col[ 0 ]; img.data[ i + 1 ] = col[ 1 ]; img.data[ i + 2 ] = col[ 2 ];
        img.data[ i + 3 ] = Math.round( Math.min( 1, al ) * 255 );
      }
    }
    c.putImageData( img, 0, 0 );
    return cv;
  }

  /** (owner) the wallpaper: soft warm lights scattered still down the page, in the circle's golds and rose. */
  private drawWallLights( ctx: CanvasRenderingContext2D ): void {
    const W = this.width, H = this.height, r = this.seededLocal( 77 ), k = this.scaleK;
    const [ , rose, gold, pale ] = this.five(), cols = [ gold, pale, rose ];
    const n = Math.max( 10, Math.round( W * H / 40000 ) );
    for ( let i = 0; i < n; i++ ) {
      const x = r() * W, y = r() * H, rad = ( 8 + r() * 22 ) * k, col = cols[ Math.floor( r() * 3 ) ];
      const gl = ctx.createRadialGradient( x, y, 0, x, y, rad );
      gl.addColorStop( 0, this.rgba( this.mix( col, [ 255, 255, 240 ], 0.4 ), 0.75 ) );
      gl.addColorStop( 0.25, this.rgba( col, 0.35 ) );
      gl.addColorStop( 1, this.rgba( col, 0 ) );
      ctx.fillStyle = gl;
      ctx.fillRect( x - rad, y - rad, rad * 2, rad * 2 );
    }
  }

  /**
   * The house, painted once: the ceiling's dark rim; the horseshoe's walls tier over tier (the stalls'
   * panelled wall, the parterre boxes, three tiers, the gallery), each tier a cream parapet dressed in gold
   * over its row of boxes; the central box and the standing room at the back; the stalls' floor, the aisle's
   * carpet and the rows of seats far to near.
   */
  private bake( scale: number, lit = false ): void {
    const W = this.width, H = this.height, F = this.F;
    const [ velvet, rose, gold, pale ] = this.five();
    const c = document.createElement( 'canvas' );
    c.width = Math.ceil( W * scale );
    c.height = Math.ceil( H * scale );
    const g = c.getContext( '2d' )!;
    g.scale( scale, scale );
    const P = ( x: number, y: number, z: number ) => this.P( x, y, z );
    const poly = ( pts: [ number, number ][], fill: string | CanvasGradient ) => {
      g.fillStyle = fill;
      g.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? g.lineTo( x, y ) : g.moveTo( x, y ) );
      g.closePath();
      g.fill();
    };
    const rr = ( seed: number ) => this.seededLocal( seed );
    // the house lights low (a dark theme): everything sinks toward a deep warm dark with the distance and
    // away from the chandelier over the middle
    const dark: RGB = this.mix( velvet, [ 18, 6, 8 ], 0.7 );
    // (owner: a darker house, its light coming from the stage) the light falls off from the stage's glow: the
    // boxes by the proscenium catch it, the house near us sinks into the dark
    const lightAt = ( x: number, y: number, z: number ) => {
      const d = Math.hypot( x * 0.6, ( y - 6 ) * 0.9, z - 26 );
      // (owner: darker at rest, brighter on the charge) the house as it is at rest, or with its lights up
      return lit ? 0.16 + 0.72 * Math.exp( -d / 14 ) : 0.03 + 0.48 * Math.exp( -d / 10 );
    };
    const tone = ( col: RGB, x: number, y: number, z: number ) => this.mix( dark, col, lightAt( x, y, z ) );
    // (owner: more of it in burgundy) the cream steeped in the velvet's red
    const cream: RGB = this.mix( this.mix( pale, [ 250, 240, 214 ], 0.45 ), this.mix( velvet, rose, 0.3 ), 0.3 );
    const red: RGB = this.mix( [ 176, 30, 36 ], velvet, 0.25 );
    const boxIn: RGB = this.mix( velvet, [ 40, 8, 12 ], 0.55 );

    // the dark behind it all: the ceiling's shadow, the house in its low light
    const bg = g.createLinearGradient( 0, 0, 0, H );
    bg.addColorStop( 0, this.rgba( this.mix( dark, gold, 0.08 ), 1 ) );
    bg.addColorStop( 1, this.rgba( dark, 1 ) );
    g.fillStyle = bg;
    g.fillRect( 0, 0, W, H );

    // the house's side walls in plan, from us to the proscenium, narrowing as they near it (owner: the camera
    // looks at the stage): the left wall going away, then across the proscenium, the right wall coming back
    const halfW = ( z: number ) => { const u = Math.max( 0, Math.min( 1, ( z - 12 ) / ( HALL.zStage - 12 ) ) ); return HALL.a - ( HALL.a - HALL.aStage ) * u * u; };
    const plan: [ number, number ][] = [];
    for ( let z = HALL.zNear; z < HALL.zStage; z += 0.4 ) { plan.push( [ -halfW( z ), z ] ); }
    plan.push( [ -HALL.aStage, HALL.zStage ] );
    const leftN = plan.length;
    plan.push( [ HALL.aStage, HALL.zStage ] );
    for ( let z = HALL.zStage - 0.4; z >= HALL.zNear; z -= 0.4 ) { plan.push( [ halfW( z ), z ] ); }
    const cum: number[] = [ 0 ];
    for ( let i = 1; i < plan.length; i++ ) { cum.push( cum[ i - 1 ] + Math.hypot( plan[ i ][ 0 ] - plan[ i - 1 ][ 0 ], plan[ i ][ 1 ] - plan[ i - 1 ][ 1 ] ) ); }
    const total = cum[ cum.length - 1 ], sMid = -1e9;
    // the two walls' stretches of the plan (the step across the proscenium between them is no wall of boxes)
    const sides: [ number, number ][] = [ [ 0, cum[ leftN - 1 ] ], [ cum[ leftN ], total ] ];
    // a point on the wall `s` metres round the plan, and `inn` metres in toward the house
    const at = ( s: number, inn = 0 ): [ number, number ] => {
      let i = 1;
      while ( i < cum.length - 1 && cum[ i ] < s ) { i++; }
      const t = ( s - cum[ i - 1 ] ) / Math.max( 1e-6, cum[ i ] - cum[ i - 1 ] );
      const x = plan[ i - 1 ][ 0 ] + ( plan[ i ][ 0 ] - plan[ i - 1 ][ 0 ] ) * t, z = plan[ i - 1 ][ 1 ] + ( plan[ i ][ 1 ] - plan[ i - 1 ][ 1 ] ) * t;
      const tx = plan[ i ][ 0 ] - plan[ i - 1 ][ 0 ], tz = plan[ i ][ 1 ] - plan[ i - 1 ][ 1 ], tl = Math.hypot( tx, tz ) || 1;
      // the inward normal: to the right of the way round (the plan runs left wall, across, right wall)
      return [ x + tz / tl * inn, z - tx / tl * inn ];
    };
    const W3 = ( s: number, y: number, inn = 0 ) => { const [ x, z ] = at( s, inn ); return P( x, y, z ); };
    // a band of the wall from s0 to s1 between two heights, in steps so its curve stays round
    const band = ( s0: number, s1: number, y0: number, y1: number, fill: ( s: number ) => string, inn = 0, step = 0.35 ) => {
      for ( let s = s0; s < s1 - 1e-6; s += step ) {
        const s2 = Math.min( s1, s + step + 0.02 );
        const [ x, z ] = at( ( s + s2 ) / 2, inn );
        if ( z < 0.6 ) { continue; }
        poly( [ W3( s, y0, inn ), W3( s2, y0, inn ), W3( s2, y1, inn ), W3( s, y1, inn ) ], fill( s ) );
        void x;
      }
    };
    const bandS = ( y0: number, y1: number, fill: ( s: number ) => string, inn = 0, step = 0.35 ) => { for ( const [ a0, a1 ] of sides ) { band( a0, a1, y0, y1, fill, inn, step ); } };
    const colAt = ( col: RGB, s: number, y: number, inn = 0 ) => { const [ x, z ] = at( s, inn ); return this.rgba( tone( col, x, y, z ), 1 ); };
    // where the central box and the standing room sit, at the back's middle
    const MID_BOX = 2.6, MID_STAND = 4.6;

    // ---- the ceiling's rim, gilded, over the gallery ----
    bandS( CEIL - 0.6, CEIL + 3, s => colAt( this.mix( dark, gold, 0.35 ), s, CEIL ) );
    bandS( CEIL - 0.8, CEIL - 0.6, s => colAt( gold, s, CEIL ) );

    // ---- the tiers ----
    this.lamps = [];
    const lampR = rr( 9 );
    for ( let ti = 0; ti < TIERS.length; ti++ ) {
      const f = TIERS[ ti ], top = ti + 1 < TIERS.length ? TIERS[ ti + 1 ] : CEIL - 0.8;
      const pTop = f + PARAPET, soffit = top - 0.42;
      // the boxes behind: the deep red room, its ceiling's shadow, the curtains drawn to each side, the slim
      // cream columns between the boxes
      const boxLen = 2.3;
      // (kaizen 1) the room darker up under its ceiling, a little warmer low where the house's light reaches
      const boxH = soffit - pTop;
      for ( const [ y0, y1, k0 ] of [ [ pTop, pTop + boxH * 0.45, 0.75 ], [ pTop + boxH * 0.45, pTop + boxH * 0.75, 1 ], [ pTop + boxH * 0.75, soffit, 1.25 ] ] ) {
        bandS( y0, y1, s => {
          const inMid = Math.abs( s - sMid ) < MID_BOX && ( ti === 1 || ti === 2 );
          return colAt( this.mix( inMid ? this.mix( red, boxIn, 0.25 ) : boxIn, [ 14, 2, 4 ], ( k0 - 0.75 ) * 0.9 ), s, ( pTop + soffit ) / 2 );
        }, 0, 0.3 );
      }
      // the curtains: a drape of red velvet hanging full length at each side of every box, pleated, and a
      // scalloped valance across its head
      for ( const [ q0, q1 ] of sides ) for ( let s = q0 + boxLen / 2; s < q1; s += boxLen ) {
        if ( ( ti === 1 || ti === 2 ) && Math.abs( s - sMid ) < MID_BOX + 0.6 ) { continue; }
        const [ bx0, z ] = at( s );
        if ( z < 0.9 ) { continue; }
        const cc = tone( red, bx0, soffit, z );
        for ( const side of [ -1, 1 ] ) {
          const outer = s + side * boxLen * 0.47, inner = s + side * boxLen * 0.2;
          const pts: [ number, number ][] = [ W3( outer, soffit, 0.05 ), W3( inner, soffit, 0.05 ) ];
          for ( let q = 1; q <= 6; q++ ) { const u = q / 6; pts.push( W3( inner + side * 0.05 * Math.sin( u * Math.PI ), soffit + ( pTop - soffit ) * u, 0.05 ) ); }
          pts.push( W3( outer, pTop, 0.05 ) );
          const [ px0, py0 ] = W3( outer, soffit, 0.05 ), [ px1 ] = W3( inner, soffit, 0.05 );
          const dg = g.createLinearGradient( px0, py0, px1, py0 );
          for ( let q = 0; q <= 6; q++ ) { dg.addColorStop( q / 6, this.rgba( q % 2 ? this.mix( cc, rose, 0.16 ) : this.mix( cc, [ 20, 4, 6 ], 0.35 ), 1 ) ); }
          poly( pts, dg );
        }
        const vl: [ number, number ][] = [];
        const v0 = s - boxLen * 0.47, v1 = s + boxLen * 0.47;
        for ( let q = 0; q <= 12; q++ ) { vl.push( W3( v0 + ( v1 - v0 ) * q / 12, soffit, 0.06 ) ); }
        for ( let q = 36; q >= 0; q-- ) { const u = q / 36, dip = 0.12 + 0.24 * Math.abs( Math.sin( u * Math.PI * 3 ) ); vl.push( W3( v0 + ( v1 - v0 ) * u, soffit - dip, 0.06 ) ); }
        poly( vl, this.rgba( this.mix( cc, rose, 0.1 ), 1 ) );
      }
      for ( const [ q0, q1 ] of sides ) for ( let s = q0 + boxLen / 2; s < q1; s += boxLen ) {
        if ( ( ti === 1 || ti === 2 ) && Math.abs( s - sMid ) < MID_BOX + 0.6 ) { continue; }
        if ( at( s )[ 1 ] < 0.9 ) { continue; }
        // the column between this box and the next
        const sc = s + boxLen / 2;
        // (kaizen 1) round, lit from the chandelier: a shaded flank, the lit face, a soft far flank
        band( sc - 0.13, sc - 0.05, pTop, soffit, ss => colAt( this.mix( cream, [ 90, 60, 40 ], 0.35 ), ss, ( pTop + soffit ) / 2, 0.1 ), 0.1, 0.08 );
        band( sc - 0.05, sc + 0.06, pTop, soffit, ss => colAt( this.mix( cream, [ 255, 250, 236 ], 0.3 ), ss, ( pTop + soffit ) / 2, 0.1 ), 0.1, 0.11 );
        band( sc + 0.06, sc + 0.13, pTop, soffit, ss => colAt( this.mix( cream, [ 120, 90, 60 ], 0.18 ), ss, ( pTop + soffit ) / 2, 0.1 ), 0.1, 0.07 );
      }
      // the soffit's gilded fascia under the tier above
      bandS( soffit, top, s => colAt( this.mix( cream, gold, 0.25 ), s, top ) );
      bandS( soffit, soffit + 0.07, s => colAt( this.mix( dark, gold, 0.5 ), s, soffit ) );
      // the parapet: the cream face, a moulded rail on top catching the light, a shadow under it, panels with
      // a golden garland each and a small lamp under the garland's knot
      if ( ti === 0 ) {
        // the parterre boxes' parapet over the stalls' panelled wall
        bandS( 0, f, s => colAt( this.mix( cream, [ 200, 184, 150 ], 0.3 ), s, f / 2 ) );
        // the wall's grilles low down, dark slats
        const gr = rr( 3 );
        void gr;
        for ( const [ q0, q1 ] of sides ) for ( let s = q0; s < q1; s += 0.18 ) {
          if ( Math.abs( s - sMid ) < MID_STAND ) { continue; }
          band( s, s + 0.06, 0.25, 0.95, ss => colAt( [ 70, 52, 40 ], ss, 0.6 ), 0, 0.06 );
        }
        bandS( 0.95, 1.05, s => colAt( this.mix( cream, gold, 0.3 ), s, 1 ) );
        // (kaizen 4) the doors along the stalls' walls, deep plum in gilded frames, as in the photograph
        // (owner: the doors level on both sides, clear of the pit's rail) at the same depths on each wall
        const sAtZ = ( side: number, zz: number ) => {
          let best = 0, bd = 1e9;
          for ( let i = 0; i < plan.length; i++ ) { if ( Math.sign( plan[ i ][ 0 ] ) === side && Math.abs( plan[ i ][ 1 ] - zz ) < bd ) { bd = Math.abs( plan[ i ][ 1 ] - zz ); best = cum[ i ]; } }
          return best;
        };
        for ( const side of [ -1, 1 ] ) {
          for ( const zz of [ 4.2, 8.6, 13, 17.4 ] ) {
            const s = sAtZ( side, zz );
            const d0 = s - 0.6, d1 = s + 0.6;
            band( d0 - 0.1, d1 + 0.1, 0, 2.5, ss => colAt( this.mix( gold, cream, 0.3 ), ss, 1.2, 0.02 ), 0.02, 0.2 );
            band( d0, d1, 0, 2.4, ss => colAt( this.mix( [ 92, 34, 64 ], velvet, 0.3 ), ss, 1.2, 0.03 ), 0.03, 0.2 );
            band( s - 0.03, s + 0.03, 0, 2.4, ss => colAt( [ 40, 14, 28 ], ss, 1.2, 0.04 ), 0.04, 0.06 );
            for ( const [ y0, y1 ] of [ [ 0.3, 1.1 ], [ 1.3, 2.2 ] ] ) {
              for ( const [ a0, a1 ] of [ [ d0 + 0.1, s - 0.1 ], [ s + 0.1, d1 - 0.1 ] ] ) {
                band( a0, a1, y0, y0 + 0.04, ss => colAt( gold, ss, y0, 0.04 ), 0.04, 0.25 );
                band( a0, a1, y1 - 0.04, y1, ss => colAt( gold, ss, y1, 0.04 ), 0.04, 0.25 );
              }
            }
          }
        }
        bandS( f - 0.12, f, s => colAt( this.mix( cream, gold, 0.4 ), s, f ) );
      }
      // (the central box runs through the second tier: no parapet across it there)
      const spans = sides;
      // (kaizen 1) the parapet stands out over the house: its underside shows from below, gilded in the shade;
      // its face darker low, lit up to the moulded rail, a shadow line under the rail
      const PJ = ti === 0 ? 0.2 : 0.6;
      for ( const [ p0, p1 ] of spans ) {
        if ( ti > 0 ) {
          for ( let s = p0; s < p1 - 1e-6; s += 0.35 ) {
            const s2 = Math.min( p1, s + 0.37 );
            if ( at( s, 0 )[ 1 ] < 0.6 ) { continue; }
            poly( [ W3( s, f, 0 ), W3( s2, f, 0 ), W3( s2, f, PJ ), W3( s, f, PJ ) ], colAt( this.mix( this.mix( dark, gold, 0.3 ), cream, 0.2 ), s, f - 1 ) );
          }
        }
        band( p0, p1, f, f + 0.3, s => colAt( this.mix( cream, [ 120, 90, 60 ], 0.25 ), s, f + 0.2, PJ ), PJ );
        band( p0, p1, f + 0.3, pTop - 0.16, s => colAt( cream, s, f + 0.6, PJ ), PJ );
        band( p0, p1, pTop - 0.18, pTop - 0.14, s => colAt( this.mix( cream, [ 70, 40, 30 ], 0.45 ), s, pTop, PJ ), PJ );
        band( p0, p1, pTop - 0.14, pTop, s => colAt( this.mix( cream, [ 255, 250, 236 ], 0.45 ), s, pTop, PJ + 0.04 ), PJ + 0.04 );
        band( p0, p1, f, f + 0.08, s => colAt( this.mix( dark, gold, 0.45 ), s, f, PJ ), PJ );
      }
      // the panels: their gilded frames, a garland swag with its knot and a small lamp hung under it
      const panel = 1.6;
      for ( const [ q0, q1 ] of sides ) for ( let s = q0 + panel / 2; s < q1; s += panel ) {
        const [ x, z ] = at( s, PJ + 0.01 );
        if ( z < 1.1 || ( ti === 2 && Math.abs( s - sMid ) < MID_BOX + 0.3 ) ) { continue; }
        const k = F / z;
        if ( k < 3 ) { continue; }
        const gc = this.rgba( tone( gold, x, f + 0.6, z ), 1 );
        g.strokeStyle = gc;
        g.lineWidth = Math.max( 0.5, k * 0.03 );
        const fr: [ number, number ][] = [ W3( s - panel * 0.42, f + 0.18, PJ + 0.01 ), W3( s + panel * 0.42, f + 0.18, PJ + 0.01 ), W3( s + panel * 0.42, pTop - 0.2, PJ + 0.01 ), W3( s - panel * 0.42, pTop - 0.2, PJ + 0.01 ) ];
        g.beginPath(); fr.forEach( ( [ px, py ], i ) => i ? g.lineTo( px, py ) : g.moveTo( px, py ) ); g.closePath(); g.stroke();
        // the garland: two swags of leaves falling from the panel's corners to its knot
        g.lineWidth = Math.max( 0.7, k * 0.07 );
        for ( const side of [ -1, 1 ] ) {
          const a = W3( s + side * panel * 0.36, pTop - 0.28, PJ + 0.01 ), m = W3( s + side * panel * 0.18, f + 0.45, PJ + 0.01 ), b = W3( s, pTop - 0.38, PJ + 0.01 );
          // (kaizen 1) in relief: the gilt in its shade, its lit crest along the top
          g.strokeStyle = this.rgba( tone( this.mix( gold, [ 80, 50, 16 ], 0.3 ), x, f + 0.6, z ), 1 );
          g.lineWidth = Math.max( 0.8, k * 0.1 );
          g.beginPath(); g.moveTo( a[ 0 ], a[ 1 ] ); g.quadraticCurveTo( m[ 0 ], m[ 1 ], b[ 0 ], b[ 1 ] ); g.stroke();
          g.strokeStyle = this.rgba( tone( this.mix( gold, pale, 0.5 ), x, f + 0.6, z ), 1 );
          g.lineWidth = Math.max( 0.5, k * 0.045 );
          g.beginPath(); g.moveTo( a[ 0 ], a[ 1 ] - k * 0.025 ); g.quadraticCurveTo( m[ 0 ], m[ 1 ] - k * 0.025, b[ 0 ], b[ 1 ] - k * 0.025 ); g.stroke();
          if ( k > 9 ) {
            // its leaves in small dabs along the swag
            g.fillStyle = gc;
            for ( let q = 1; q < 6; q++ ) {
              const u = q / 6, px = ( 1 - u ) * ( 1 - u ) * a[ 0 ] + 2 * u * ( 1 - u ) * m[ 0 ] + u * u * b[ 0 ], py = ( 1 - u ) * ( 1 - u ) * a[ 1 ] + 2 * u * ( 1 - u ) * m[ 1 ] + u * u * b[ 1 ];
              g.beginPath(); g.ellipse( px, py, k * 0.07, k * 0.045, side * 0.6, 0, Math.PI * 2 ); g.fill();
            }
          }
        }
        // the knot, a gilded rosette with its ribbon tails
        const [ kx, ky ] = W3( s, pTop - 0.36, PJ + 0.01 );
        g.fillStyle = gc;
        g.beginPath(); g.arc( kx, ky, Math.max( 0.8, k * 0.09 ), 0, Math.PI * 2 ); g.fill();
        // (owner: no single lights crowding the stage's frame) the last panels by the proscenium keep no lamp
        const ph = lampR() * Math.PI * 2;
        if ( z <= HALL.zStage - 1.0 ) {
          // (kaizen) the sconce under the knot: its warm light on the parapet round it, a gilded bracket and
          // two small shaded bulbs, lit by themselves whatever the house's light
          const [ lx, ly ] = W3( s, f + 0.42, PJ + 0.1 );
          const pool = g.createRadialGradient( lx, ly, 0, lx, ly, k * 0.65 );
          pool.addColorStop( 0, this.rgba( [ 255, 210, 150 ], lit ? 0.22 : 0.08 ) );
          pool.addColorStop( 1, this.rgba( [ 255, 210, 150 ], 0 ) );
          g.fillStyle = pool;
          g.fillRect( lx - k * 0.65, ly - k * 0.65, k * 1.3, k * 1.3 );
          if ( k > 6 ) {
            g.strokeStyle = this.rgba( this.mix( tone( gold, x, f, z ), [ 255, 210, 150 ], 0.25 ), 1 );
            g.lineWidth = Math.max( 0.5, k * 0.025 );
            // (kaizen: two bulbs under the swags read as a face) one tulip shade on a short curled arm
            g.beginPath(); g.moveTo( lx, ly + k * 0.22 ); g.quadraticCurveTo( lx + k * 0.08, ly + k * 0.12, lx, ly + k * 0.05 ); g.stroke();
            g.fillStyle = this.rgba( this.mix( dark, [ 255, 232, 184 ], lit ? 0.9 : 0.5 ), 1 );
            g.beginPath();
            g.moveTo( lx - k * 0.07, ly - k * 0.12 ); g.quadraticCurveTo( lx - k * 0.08, ly + k * 0.02, lx, ly + k * 0.05 );
            g.quadraticCurveTo( lx + k * 0.08, ly + k * 0.02, lx + k * 0.07, ly - k * 0.12 ); g.quadraticCurveTo( lx, ly - k * 0.08, lx - k * 0.07, ly - k * 0.12 );
            g.closePath(); g.fill();
          } else {
            g.fillStyle = this.rgba( this.mix( dark, [ 255, 232, 184 ], lit ? 0.9 : 0.5 ), 1 );
            g.beginPath(); g.ellipse( lx, ly, Math.max( 0.6, k * 0.08 ), Math.max( 0.6, k * 0.11 ), 0, 0, Math.PI * 2 ); g.fill();
          }
          // (owner: no lights alone on the wall) a lamp the proscenium's wall hides keeps no glow
          const [ lmx, lmz ] = at( s, PJ + 0.1 );
          if ( Math.abs( lmx ) / lmz > HALL.aStage / HALL.zStage - 0.004 ) { this.lamps.push( { x: lx, y: ly, r: Math.max( 3, k * 0.42 ), phase: ph } ); }
        }
      }
    }
    // ---- the stage through the proscenium (owner: the camera looks at the stage): its dark depth, the
    // boards, the black legs at its sides; the house curtain drawn aside to left and right (owner) under its
    // swagged pelmet with the gold fringe; the proscenium's gilded frame round it; the orchestra pit before it ----
    const z0 = HALL.zStage, hs = STAGE.half, fl = STAGE.floor, tp = STAGE.top;
    {
      const zb = z0 + STAGE.deep;
      const [ ox0, oy0 ] = P( -hs, tp, z0 ), [ ox1, oy1 ] = P( hs, fl, z0 );
      g.save();
      g.beginPath(); g.rect( ox0, oy0, ox1 - ox0, oy1 - oy0 ); g.clip();
      const sg = g.createLinearGradient( 0, oy0, 0, oy1 );
      sg.addColorStop( 0, this.rgba( this.mix( dark, [ 0, 0, 0 ], 0.72 ), 1 ) );
      sg.addColorStop( 1, this.rgba( this.mix( dark, [ 0, 0, 0 ], 0.35 ), 1 ) );
      g.fillStyle = sg;
      g.fillRect( ox0, oy0, ox1 - ox0, oy1 - oy0 );
      // the boards, dark wood, their joints running away from us
      const [ , by0 ] = P( 0, fl, z0 ), [ , by1 ] = P( 0, fl, zb );
      const bg2 = g.createLinearGradient( 0, by1, 0, by0 );
      bg2.addColorStop( 0, this.rgba( this.mix( dark, [ 0, 0, 0 ], 0.4 ), 1 ) );
      bg2.addColorStop( 1, this.rgba( this.mix( dark, [ 90, 40, 36 ], 0.3 ), 1 ) );
      poly( [ P( -hs * 1.6, fl, z0 ), P( hs * 1.6, fl, z0 ), P( hs * 1.6, fl, zb ), P( -hs * 1.6, fl, zb ) ], bg2 );
      g.strokeStyle = this.rgba( this.mix( dark, [ 0, 0, 0 ], 0.5 ), 0.6 );
      g.lineWidth = 0.6;
      for ( let x = -hs * 1.5; x <= hs * 1.5; x += 0.7 ) { const [ ax, ay ] = P( x, fl, z0 ), [ bx2, by2 ] = P( x, fl, zb ); g.beginPath(); g.moveTo( ax, ay ); g.lineTo( bx2, by2 ); g.stroke(); }
      // the back wall and the legs and borders masking the wings and the flies, black, their edges just seen
      poly( [ P( -hs * 2, fl, zb ), P( hs * 2, fl, zb ), P( hs * 2, tp + 4, zb ), P( -hs * 2, tp + 4, zb ) ], this.rgba( this.mix( dark, [ 0, 0, 0 ], 0.55 ), 1 ) );
      for ( const zl of [ z0 + 11, z0 + 7, z0 + 3 ] ) {
        const legC = this.rgba( this.mix( dark, [ 0, 0, 0 ], 0.45 ), 1 ), edge = this.rgba( this.mix( dark, velvet, 0.3 ), 1 );
        for ( const side of [ -1, 1 ] ) {
          poly( [ P( side * ( hs - 0.6 ), fl, zl ), P( side * ( hs + 3 ), fl, zl ), P( side * ( hs + 3 ), tp + 2, zl ), P( side * ( hs - 0.6 ), tp + 2, zl ) ], legC );
          poly( [ P( side * ( hs - 0.6 ), fl, zl ), P( side * ( hs - 0.5 ), fl, zl ), P( side * ( hs - 0.5 ), tp + 2, zl ), P( side * ( hs - 0.6 ), tp + 2, zl ) ], edge );
        }
        poly( [ P( -hs * 2, tp - 1.4, zl ), P( hs * 2, tp - 1.4, zl ), P( hs * 2, tp + 2, zl ), P( -hs * 2, tp + 2, zl ) ], legC );
      }
      g.restore();
      // (owner's photograph) the house curtain in two drapes of deep red velvet hung from the top, swept up
      // from the middle to each side in one long curve and falling straight from there to the floor, pleated
      // (owner: the half-clear ellipse on the stage removed) no working light's wash on the boards
      // (kaizen 3) the sweep round at its head as in the photograph, not a pointed arch
      // (owner: what lies over what) the curtain hangs just behind the proscenium's opening
      const zc = z0 + 0.3;
      for ( const side of [ -1, 1 ] ) {
        const gather = fl + ( tp - fl ) * 0.42;
        const inner = ( y: number ) => {
          if ( y >= gather ) { const u = ( tp - y ) / ( tp - gather ); return 0.7 + ( hs - 1.9 ) * Math.pow( Math.sin( u * Math.PI / 2 ), 0.55 ); }
          const u = ( gather - y ) / ( gather - fl ); return hs - 1.2 + 0.25 * u;
        };
        const pts: [ number, number ][] = [ P( side * ( hs + 0.3 ), tp + 0.2, zc ) ];
        for ( let q = 0; q <= 48; q++ ) { const y = tp + 0.2 - ( tp + 0.2 - fl ) * q / 48; pts.push( P( side * inner( Math.min( tp, y ) ), y, zc ) ); }
        pts.push( P( side * ( hs + 0.3 ), fl, zc ) );
        const [ px0, py0 ] = P( side * ( hs + 0.3 ), tp, zc ), [ px1 ] = P( side * 0.7, tp, zc );
        const cg2 = g.createLinearGradient( px0, py0, px1, py0 );
        const cc = this.mix( this.mix( red, [ 120, 20, 22 ], 0.4 ), dark, 0.5 );
        for ( let q = 0; q <= 14; q++ ) { cg2.addColorStop( q / 14, this.rgba( q % 2 ? this.mix( cc, rose, 0.12 ) : this.mix( cc, [ 14, 2, 4 ], 0.5 ), 1 ) ); }
        poly( pts, cg2 );
        // the swept part's folds follow its curve; the hanging part darker in the wing's shadow
        g.save();
        g.beginPath(); pts.forEach( ( [ x, y ], q ) => q ? g.lineTo( x, y ) : g.moveTo( x, y ) ); g.closePath(); g.clip();
        g.strokeStyle = this.rgba( this.mix( cc, [ 10, 2, 4 ], 0.55 ), 0.55 );
        g.lineWidth = Math.max( 0.6, F / zc * 0.07 );
        for ( let q = 1; q <= 5; q++ ) {
          const d = q / 6;
          g.beginPath();
          for ( let w = 0; w <= 30; w++ ) {
            const y = tp - ( tp - gather ) * w / 30, xi = inner( y ), xx = xi + ( hs + 0.3 - xi ) * d;
            const [ sx, sy ] = P( side * xx, y, zc );
            if ( w ) { g.lineTo( sx, sy ); } else { g.moveTo( sx, sy ); }
          }
          g.stroke();
        }
        const [ , gy ] = P( 0, gather, zc ), [ , fy ] = P( 0, fl, zc );
        const hg = g.createLinearGradient( 0, gy, 0, fy );
        hg.addColorStop( 0, this.rgba( [ 10, 2, 4 ], 0.1 ) );
        hg.addColorStop( 1, this.rgba( [ 10, 2, 4 ], 0.4 ) );
        g.fillStyle = hg;
        g.fillRect( Math.min( px0, px1 ) - 4, gy, Math.abs( px1 - px0 ) + 8, fy - gy );
        // (kaizen 2) the velvet's sheen along the swept edge, where the pile turns to the light
        g.strokeStyle = this.rgba( this.mix( cc, rose, 0.35 ), 0.45 );
        g.lineWidth = Math.max( 0.8, F / zc * 0.18 );
        g.beginPath();
        for ( let w = 0; w <= 30; w++ ) {
          const y = tp - ( tp - gather ) * w / 30, [ sx, sy ] = P( side * ( inner( y ) + 0.12 ), y, zc );
          if ( w ) { g.lineTo( sx, sy ); } else { g.moveTo( sx, sy ); }
        }
        g.stroke();
        // (kaizen) the stage's light licking the drape's inner edge as it hangs
        g.strokeStyle = this.rgba( this.mix( cc, [ 255, 190, 140 ], 0.35 ), 0.55 );
        g.lineWidth = Math.max( 0.8, F / zc * 0.12 );
        g.beginPath();
        for ( let w = 0; w <= 30; w++ ) {
          const y = gather + 1 - ( gather + 1 - fl ) * w / 30, [ sx, sy ] = P( side * ( inner( y ) + 0.05 ), y, zc );
          if ( w ) { g.lineTo( sx, sy ); } else { g.moveTo( sx, sy ); }
        }
        g.stroke();
        g.restore();
        // (kaizen 2) the gold fringe along the hem
        const [ hx0, hy0 ] = P( side * inner( fl ), fl + 0.22, zc - 0.02 ), [ hx1, hy1 ] = P( side * ( hs + 0.3 ), fl, zc - 0.02 );
        g.fillStyle = this.rgba( this.mix( gold, dark, 0.45 ), 1 );
        g.fillRect( Math.min( hx0, hx1 ), hy0, Math.abs( hx1 - hx0 ), hy1 - hy0 );
        g.strokeStyle = this.rgba( this.mix( gold, dark, 0.65 ), 1 );
        g.lineWidth = 0.6;
        for ( let x = Math.min( hx0, hx1 ); x < Math.max( hx0, hx1 ); x += 2 ) { g.beginPath(); g.moveTo( x, hy0 ); g.lineTo( x, hy1 ); g.stroke(); }
      }
      // (owner's photograph) the proscenium: pale cream in the dark house, framed in thin gold; over the
      // opening a tall fluted panel with its rounded upper corners; each side a broad pilaster with a long dark
      // grille in it; the spotlight rigs on the boxes' tops at its upper corners; the stage's front below
      {
        const wall = this.mix( this.mix( cream, velvet, 0.25 ), dark, 0.72 );
        const [ ax, ay ] = P( -HALL.aStage, CEIL + 3, z0 ), [ bx2, by2 ] = P( HALL.aStage, 0, z0 );
        const [ hx0, hy0 ] = P( -hs, tp + 0.2, z0 ), [ hx1, hy1 ] = P( hs, fl, z0 );
        const wg = g.createLinearGradient( 0, ay, 0, by2 );
        wg.addColorStop( 0, this.rgba( this.mix( wall, dark, 0.6 ), 1 ) );
        wg.addColorStop( 0.45, this.rgba( wall, 1 ) );
        wg.addColorStop( 1, this.rgba( this.mix( wall, dark, 0.25 ), 1 ) );
        g.fillStyle = wg;
        g.beginPath(); g.rect( ax, ay, bx2 - ax, by2 - ay ); g.rect( hx0, hy0, hx1 - hx0, hy1 - hy0 ); g.fill( 'evenodd' );
        const [ , fy0 ] = P( 0, fl, z0 ), [ , fy1 ] = P( 0, 0, z0 );
        g.fillStyle = this.rgba( this.mix( dark, [ 0, 0, 0 ], 0.3 ), 1 );
        g.fillRect( hx0, fy0, hx1 - hx0, fy1 - fy0 );
        // the stage's front edge, a lip of lit wood
        const [ , ey0 ] = P( 0, fl, z0 - 0.05 );
        g.fillStyle = this.rgba( this.mix( dark, [ 150, 110, 70 ], 0.35 ), 1 );
        g.fillRect( hx0, ey0 - 1, hx1 - hx0, Math.max( 1.2, F / z0 * 0.1 ) );
        const k = F / z0;
        const goldLine = ( pts: [ number, number ][], w: number, col: RGB ) => {
          g.strokeStyle = this.rgba( col, 1 );
          g.lineWidth = Math.max( 0.6, k * w );
          g.beginPath(); pts.forEach( ( [ x, y ], q ) => q ? g.lineTo( x, y ) : g.moveTo( x, y ) ); g.stroke();
        };
        const gl1 = this.mix( gold, dark, 0.4 ), gl2 = this.mix( gold, dark, 0.6 );
        // (owner: the frame round the curtain, after real photographs) the Staatsoper's proscenium in its layers:
        // the opening's deep reveal, a slim gilt bead and a cream fascia round it; over it a straight frieze of
        // fine flutes with its two dark slots, dentils under it and a projecting cornice over it; above that a
        // low arch in three stepped archivolts with the lighting bridge's slot in its soffit, the reeded wall
        // round it; each side a broad pilaster on its plinth, fluted beside its tall lattice grille, crowned by
        // a capital at the frieze
        const hiC = this.mix( wall, [ 255, 246, 222 ], 0.14 ), loC = this.mix( wall, dark, 0.4 ), deepC = this.mix( wall, dark, 0.65 );
        const D = 0.55;
        // the reveal: the opening's sides and head have depth, seen from the stalls
        for ( const side of [ -1, 1 ] ) {
          poly( [ P( side * hs, fl, z0 ), P( side * hs, fl, z0 + D ), P( side * hs, tp + 0.2, z0 + D ), P( side * hs, tp + 0.2, z0 ) ], this.rgba( loC, 1 ) );
        }
        poly( [ P( -hs, tp + 0.2, z0 ), P( hs, tp + 0.2, z0 ), P( hs, tp + 0.2, z0 + D ), P( -hs, tp + 0.2, z0 + D ) ], this.rgba( deepC, 1 ) );
        // the fascia round the opening, the gilt bead on its inner edge and a fillet on its outer
        const fw2 = 0.4;
        g.fillStyle = this.rgba( hiC, 1 );
        {
          const [ o0x, o0y ] = P( -hs - fw2, tp + 0.2 + fw2, z0 - 0.01 ), [ o1x, o1y ] = P( hs + fw2, fl, z0 - 0.01 );
          const [ i0x, i0y ] = P( -hs, tp + 0.2, z0 - 0.01 ), [ i1x ] = P( hs, fl, z0 - 0.01 );
          g.beginPath(); g.rect( o0x, o0y, o1x - o0x, o1y - o0y ); g.rect( i0x, i0y, i1x - i0x, o1y - i0y ); g.fill( 'evenodd' );
        }
        goldLine( [ P( -hs - 0.06, fl, z0 - 0.02 ), P( -hs - 0.06, tp + 0.26, z0 - 0.02 ), P( hs + 0.06, tp + 0.26, z0 - 0.02 ), P( hs + 0.06, fl, z0 - 0.02 ) ], 0.1, gl1 );
        goldLine( [ P( -hs - fw2, fl, z0 - 0.02 ), P( -hs - fw2, tp + 0.2 + fw2, z0 - 0.02 ), P( hs + fw2, tp + 0.2 + fw2, z0 - 0.02 ), P( hs + fw2, fl, z0 - 0.02 ) ], 0.04, gl2 );
        // the frieze: straight, finely fluted, its two dark slots
        const pt0 = tp + 0.2 + fw2 + 0.12, pt1 = tp + 2.8;
        const fz: [ number, number ][] = [ P( -hs - fw2, pt0, z0 - 0.02 ), P( hs + fw2, pt0, z0 - 0.02 ), P( hs + fw2, pt1, z0 - 0.02 ), P( -hs - fw2, pt1, z0 - 0.02 ) ];
        const [ , fzy0 ] = fz[ 0 ], [ , fzy1 ] = fz[ 2 ];
        const fg2 = g.createLinearGradient( 0, fzy1, 0, fzy0 );
        fg2.addColorStop( 0, this.rgba( loC, 1 ) );
        fg2.addColorStop( 0.3, this.rgba( hiC, 1 ) );
        fg2.addColorStop( 1, this.rgba( wall, 1 ) );
        poly( fz, fg2 );
        g.strokeStyle = this.rgba( loC, 0.9 );
        g.lineWidth = Math.max( 0.4, k * 0.025 );
        for ( let x = -hs - fw2 + 0.1; x < hs + fw2; x += 0.14 ) { const [ fx0, fyy0 ] = P( x, pt0 + 0.12, z0 - 0.02 ), [ , fyy1 ] = P( x, pt1 - 0.12, z0 - 0.02 ); g.beginPath(); g.moveTo( fx0, fyy0 ); g.lineTo( fx0, fyy1 ); g.stroke(); }
        for ( const side of [ -1, 1 ] ) {
          const [ qx0, qy0 ] = P( side * 1.7 - 0.24, pt1 - 0.45, z0 - 0.03 ), [ qx1, qy1 ] = P( side * 1.7 + 0.24, pt0 + 0.55, z0 - 0.03 );
          g.fillStyle = this.rgba( this.mix( dark, [ 0, 0, 0 ], 0.45 ), 1 );
          g.fillRect( Math.min( qx0, qx1 ), qy0, Math.abs( qx1 - qx0 ), qy1 - qy0 );
          goldLine( [ [ Math.min( qx0, qx1 ) - 1, qy0 - 1 ], [ Math.max( qx0, qx1 ) + 1, qy0 - 1 ], [ Math.max( qx0, qx1 ) + 1, qy1 + 1 ], [ Math.min( qx0, qx1 ) - 1, qy1 + 1 ], [ Math.min( qx0, qx1 ) - 1, qy0 - 1 ] ], 0.03, gl1 );
        }
        // dentils under the frieze, the cornice over it with its shadow
        for ( let x = -hs - fw2; x < hs + fw2; x += 0.22 ) { poly( [ P( x, pt0, z0 - 0.03 ), P( x + 0.12, pt0, z0 - 0.03 ), P( x + 0.12, pt0 + 0.12, z0 - 0.03 ), P( x, pt0 + 0.12, z0 - 0.03 ) ], this.rgba( hiC, 1 ) ); }
        poly( [ P( -HALL.aStage, pt1, z0 - 0.05 ), P( HALL.aStage, pt1, z0 - 0.05 ), P( HALL.aStage, pt1 + 0.32, z0 - 0.2 ), P( -HALL.aStage, pt1 + 0.32, z0 - 0.2 ) ], this.rgba( hiC, 1 ) );
        poly( [ P( -HALL.aStage, pt1 - 0.08, z0 - 0.03 ), P( HALL.aStage, pt1 - 0.08, z0 - 0.03 ), P( HALL.aStage, pt1, z0 - 0.05 ), P( -HALL.aStage, pt1, z0 - 0.05 ) ], this.rgba( deepC, 1 ) );
        goldLine( [ P( -HALL.aStage, pt1 + 0.32, z0 - 0.2 ), P( HALL.aStage, pt1 + 0.32, z0 - 0.2 ) ], 0.05, gl1 );
        // the low arch over the cornice: its soffit recessed, the lighting bridge's slot in it, three stepped
        // archivolts round it, the reeded wall in its spandrels
        {
          const ys = pt1 + 0.45, span = hs + 1.1, rise = 2.3;
          const arch = ( off: number, n = 40 ) => { const pts: [ number, number ][] = []; for ( let q = 0; q <= n; q++ ) { const u = -1 + 2 * q / n; pts.push( [ u * ( span + off ), ys + ( rise + off * 0.6 ) * Math.sqrt( Math.max( 0, 1 - u * u ) ) ] ); } return pts; };
          // the reeded spandrels first, the arch over them
          g.strokeStyle = this.rgba( loC, 0.7 );
          g.lineWidth = Math.max( 0.4, k * 0.025 );
          for ( let x = -HALL.aStage + 0.1; x < HALL.aStage; x += 0.2 ) { const [ rx2, ry0 ] = P( x, ys, z0 - 0.02 ), [ , ry1 ] = P( x, ys + 6, z0 - 0.02 ); g.beginPath(); g.moveTo( rx2, ry0 ); g.lineTo( rx2, ry1 ); g.stroke(); }
          for ( const [ off, col ] of [ [ 0.95, loC ], [ 0.6, hiC ], [ 0.3, wall ], [ 0, deepC ] ] as [ number, RGB ][] ) {
            const pts = arch( off ).map( ( [ x, y ] ) => P( x, y, z0 - 0.02 - off * 0.2 ) );
            pts.push( P( span + off, ys, z0 - 0.02 ), P( -span - off, ys, z0 - 0.02 ) );
            poly( pts, this.rgba( col, 1 ) );
            if ( off > 0 ) { goldLine( arch( off ).map( ( [ x, y ] ) => P( x, y, z0 - 0.03 - off * 0.2 ) ), 0.03, off === 0.6 ? gl1 : gl2 ); }
          }
          // the soffit's darker heart and the bridge's slot
          const [ sx0, sy0 ] = P( -2.6, ys + 0.75, z0 + 0.8 ), [ sx1, sy1 ] = P( 2.6, ys + 0.25, z0 + 0.8 );
          g.fillStyle = this.rgba( this.mix( dark, [ 0, 0, 0 ], 0.5 ), 1 );
          g.fillRect( sx0, sy0, sx1 - sx0, sy1 - sy0 );
          g.strokeStyle = this.rgba( deepC, 1 );
          g.lineWidth = Math.max( 0.4, k * 0.02 );
          for ( let q = 1; q < 8; q++ ) { const xx = sx0 + ( sx1 - sx0 ) * q / 8; g.beginPath(); g.moveTo( xx, sy0 ); g.lineTo( xx, sy1 ); g.stroke(); }
        }
        // the pilasters: plinth, fluted face round the tall lattice grille, capital at the frieze
        for ( const side of [ -1, 1 ] ) {
          const xa = side * ( hs + fw2 + 0.05 ), xb = side * ( HALL.aStage - 0.05 );
          const lo2 = Math.min( xa, xb ), hi2 = Math.max( xa, xb ), xm = ( xa + xb ) / 2;
          poly( [ P( lo2, 0, z0 - 0.03 ), P( hi2, 0, z0 - 0.03 ), P( hi2, pt0, z0 - 0.03 ), P( lo2, pt0, z0 - 0.03 ) ], this.rgba( wall, 1 ) );
          poly( [ P( lo2 - 0.06, 0, z0 - 0.05 ), P( hi2 + 0.06, 0, z0 - 0.05 ), P( hi2 + 0.06, fl + 0.5, z0 - 0.05 ), P( lo2 - 0.06, fl + 0.5, z0 - 0.05 ) ], this.rgba( hiC, 1 ) );
          goldLine( [ P( lo2 - 0.06, fl + 0.5, z0 - 0.06 ), P( hi2 + 0.06, fl + 0.5, z0 - 0.06 ) ], 0.04, gl1 );
          poly( [ P( lo2 - 0.08, pt0 - 0.55, z0 - 0.06 ), P( hi2 + 0.08, pt0 - 0.55, z0 - 0.06 ), P( hi2 + 0.08, pt0, z0 - 0.06 ), P( lo2 - 0.08, pt0, z0 - 0.06 ) ], this.rgba( hiC, 1 ) );
          goldLine( [ P( lo2 - 0.08, pt0 - 0.55, z0 - 0.07 ), P( hi2 + 0.08, pt0 - 0.55, z0 - 0.07 ) ], 0.04, gl1 );
          goldLine( [ P( lo2 - 0.08, pt0 - 0.28, z0 - 0.07 ), P( hi2 + 0.08, pt0 - 0.28, z0 - 0.07 ) ], 0.025, gl2 );
          g.strokeStyle = this.rgba( loC, 0.9 );
          g.lineWidth = Math.max( 0.4, k * 0.03 );
          for ( const fx of [ lo2 + 0.12, lo2 + 0.24, hi2 - 0.24, hi2 - 0.12 ] ) { const [ px2, py2 ] = P( fx, fl + 0.7, z0 - 0.04 ), [ , py3 ] = P( fx, pt0 - 0.7, z0 - 0.04 ); g.beginPath(); g.moveTo( px2, py2 ); g.lineTo( px2, py3 ); g.stroke(); }
          // the grille: a dark ground, a fine lattice of diagonals, its gilt frame
          const gw = 0.3, y0 = fl + 1.4, y1 = tp - 0.4;
          const [ gx0, gy0 ] = P( xm - gw, y1, z0 - 0.04 ), [ gx1, gy1 ] = P( xm + gw, y0, z0 - 0.04 );
          const lx0 = Math.min( gx0, gx1 ), lw = Math.abs( gx1 - gx0 );
          g.fillStyle = this.rgba( this.mix( dark, [ 0, 0, 0 ], 0.4 ), 1 );
          g.fillRect( lx0, gy0, lw, gy1 - gy0 );
          g.save();
          g.beginPath(); g.rect( lx0, gy0, lw, gy1 - gy0 ); g.clip();
          g.strokeStyle = this.rgba( this.mix( wall, dark, 0.25 ), 0.9 );
          g.lineWidth = Math.max( 0.35, k * 0.018 );
          const step = Math.max( 2, lw / 3 );
          for ( let y = gy0 - lw; y < gy1 + lw; y += step ) { g.beginPath(); g.moveTo( lx0, y ); g.lineTo( lx0 + lw, y + lw ); g.moveTo( lx0, y + lw ); g.lineTo( lx0 + lw, y ); g.stroke(); }
          g.restore();
          goldLine( [ [ lx0 - 1, gy0 - 1 ], [ lx0 + lw + 1, gy0 - 1 ], [ lx0 + lw + 1, gy1 + 1 ], [ lx0 - 1, gy1 + 1 ], [ lx0 - 1, gy0 - 1 ] ], 0.05, gl1 );
        }
        // the spotlight rigs: a black bar on the boxes' tops each side, its lamps standing on it, drawn over
        // the gilded lines behind them (owner)
        for ( const side of [ -1, 1 ] ) {
          const kk = F / RIG.z, ink = this.rgba( this.mix( dark, [ 0, 0, 0 ], 0.7 ), 1 );
          poly( [ P( side * ( RIG_LAMPS[ 0 ] - 0.3 ), RIG.y, RIG.z ), P( side * ( RIG_LAMPS[ 3 ] + 0.3 ), RIG.y, RIG.z ), P( side * ( RIG_LAMPS[ 3 ] + 0.3 ), RIG.y + 0.12, RIG.z ), P( side * ( RIG_LAMPS[ 0 ] - 0.3 ), RIG.y + 0.12, RIG.z ) ], ink );
          for ( const lx of RIG_LAMPS ) {
            const [ cx, cy ] = P( side * lx, RIG.y + 0.42, RIG.z );
            g.fillStyle = ink;
            g.beginPath(); g.ellipse( cx, cy, kk * 0.2, kk * 0.26, 0, 0, Math.PI * 2 ); g.fill();
            // its yoke down to the bar
            g.fillRect( cx - kk * 0.03, cy + kk * 0.2, kk * 0.06, kk * 0.22 );
            g.fillStyle = this.rgba( this.mix( dark, [ 90, 80, 70 ], 0.4 ), 1 );
            g.beginPath(); g.ellipse( cx - side * kk * 0.04, cy + kk * 0.05, kk * 0.12, kk * 0.15, 0, 0, Math.PI * 2 ); g.fill();
          }
        }
      }
    }
    // ---- the stalls: the floor in deep shadow, the red carpet up the aisle, the rows of seats ----
    {
      // the stalls run to the orchestra pit's rail before the stage
      const zFar = z0 - 2.2, zN = 0.7;
      const half = halfW;
      // the pit, dark, between its rail and the stage's front
      poly( [ P( -half( zFar ), 0, zFar ), P( half( zFar ), 0, zFar ), P( HALL.aStage, 0, z0 ), P( -HALL.aStage, 0, z0 ) ], this.rgba( this.mix( dark, [ 0, 0, 0 ], 0.5 ), 1 ) );
      // the floor between the walls
      const floor: [ number, number ][] = [];
      for ( let z = zN; z <= zFar; z += 0.5 ) { floor.push( P( -half( z ) - 0.2, 0, z ) ); }
      for ( let z = zFar; z >= zN; z -= 0.5 ) { floor.push( P( half( z ) + 0.2, 0, z ) ); }
      poly( floor, this.rgba( this.mix( dark, velvet, 0.35 ), 1 ) );
      // the carpet up the aisle to the pit, warm red, deeper in the dark far off
      const aw = 0.75;
      const [ c0x, c0y ] = P( -aw, 0, zN ), [ c1x ] = P( aw, 0, zN ), [ c2x, c2y ] = P( aw, 0, zFar ), [ c3x ] = P( -aw, 0, zFar );
      const cg = g.createLinearGradient( 0, c0y, 0, c2y );
      cg.addColorStop( 0, this.rgba( this.mix( [ 196, 34, 30 ], dark, 0.35 ), 1 ) );
      cg.addColorStop( 1, this.rgba( this.mix( [ 196, 34, 30 ], dark, 0.7 ), 1 ) );
      poly( [ [ c0x, c0y ], [ c1x, c0y ], [ c2x, c2y ], [ c3x, c2y ] ], cg );
      // (kaizen 3) the pit's rail as in the photograph: a low wall bowed toward the house, cream panels, its top
      // in red velvet; the orchestra's stand lights glowing up out of the pit behind it
      {
        const hw = half( zFar ) - 0.2, rz = ( x: number ) => zFar - 3.0 * ( 1 - ( x / hw ) * ( x / hw ) );
        const strip = ( y0: number, y1: number, col: RGB ) => {
          const pts: [ number, number ][] = [];
          for ( let q = 0; q <= 40; q++ ) { const x = -hw + 2 * hw * q / 40; pts.push( P( x, y0, rz( x ) ) ); }
          for ( let q = 40; q >= 0; q-- ) { const x = -hw + 2 * hw * q / 40; pts.push( P( x, y1, rz( x ) ) ); }
          poly( pts, this.rgba( col, 1 ) );
        };
        strip( 0, 1.0, this.mix( cream, dark, 0.78 ) );
        strip( 0.9, 1.0, this.mix( gold, dark, 0.55 ) );
        strip( 1.0, 1.18, this.mix( red, dark, 0.35 ) );
        strip( 1.12, 1.18, this.mix( this.mix( red, rose, 0.3 ), dark, 0.3 ) );
        g.strokeStyle = this.rgba( this.mix( gold, dark, 0.6 ), 1 );
        g.lineWidth = 0.6;
        for ( let x = -hw + 1.2; x < hw; x += 1.2 ) { const [ ax, ay ] = P( x, 0.15, rz( x ) ), [ , by ] = P( x, 0.8, rz( x ) ); g.beginPath(); g.moveTo( ax, ay ); g.lineTo( ax, by ); g.stroke(); }
      }
      // the rows, far to near, seen from behind (owner: the seats in detail): each seat's back a shell of dark
      // wood, its velvet panel in the shade with its piping, the wooden cap along its top and the stage's light
      // catching that cap's edge (more on the rows nearer the stage); the arms showing between the backs; at
      // the aisle each row's end standard with its gilded edge; far rows a band of red with its lit edge
      const rows: number[] = [];
      // (owner: the red rail before the stage gently curved) the rows start behind its bow
      for ( let z = zFar - 3.5; z > 1.6; z -= 0.92 ) { rows.push( z ); }
      const seatW = 0.56, wood: RGB = this.mix( velvet, [ 60, 22, 14 ], 0.4 );
      const rimC: RGB = this.mix( [ 255, 214, 160 ], gold, 0.25 );
      for ( const z of rows ) {
        const k = F / z, hw = half( z ) - 0.9;
        if ( hw < 1.2 ) { continue; }
        const L = lightAt( 0, 3, z ), rimA = Math.min( 1, 0.25 + L * 1.1 );
        const vel = this.mix( tone( red, 0, 1, z ), [ 14, 2, 4 ], 0.3 ), velHi = this.mix( vel, rose, 0.12 ), velLo = this.mix( vel, [ 10, 2, 4 ], 0.5 );
        const wd = this.mix( tone( wood, 0, 1, z ), [ 8, 2, 2 ], 0.25 ), cap = this.mix( wd, rimC, L * 0.5 );
        for ( const side of [ -1, 1 ] ) {
          const xs: number[] = [];
          for ( let x = aw + 0.12 + seatW / 2; x < hw; x += seatW ) { xs.push( side * x ); }
          for ( const x of xs ) {
            const x0 = x - seatW * 0.45, x1 = x + seatW * 0.45;
            const [ ax, ay ] = P( x0, 1.02, z ), [ bx, by ] = P( x1, 0.4, z );
            const w = bx - ax, h = by - ay;
            if ( k < 6 ) {
              g.fillStyle = this.rgba( vel, 1 );
              g.fillRect( ax, ay, w, h );
              g.fillStyle = this.rgba( velLo, 1 );
              g.fillRect( bx - Math.max( 0.4, w * 0.08 ), ay, Math.max( 0.4, w * 0.08 ), h );
              g.fillStyle = this.rgba( rimC, 0.5 * rimA );
              g.fillRect( ax, ay, w, Math.max( 0.5, h * 0.06 ) );
              continue;
            }
            const rad = Math.min( w, h ) * 0.34;
            const shape = ( px: number, py: number, pw: number, ph: number, pr: number ) => {
              g.beginPath();
              g.moveTo( px, py + ph );
              g.lineTo( px, py + pr );
              g.quadraticCurveTo( px, py, px + pr, py );
              g.lineTo( px + pw - pr, py );
              g.quadraticCurveTo( px + pw, py, px + pw, py + pr );
              g.lineTo( px + pw, py + ph );
              g.closePath();
            };
            // the arm to its right, seen between this back and the next: its wooden top, its post
            const [ arx, ary ] = P( x1 + seatW * 0.05, 0.72, z + 0.25 ), [ , afy ] = P( x1, 0.3, z + 0.25 );
            g.fillStyle = this.rgba( this.mix( wd, [ 6, 2, 2 ], 0.35 ), 1 );
            g.fillRect( arx - k * 0.035, ary, k * 0.07, afy - ary );
            g.fillStyle = this.rgba( cap, 1 );
            g.fillRect( arx - k * 0.05, ary - k * 0.015, k * 0.1, k * 0.035 );
            // the shell
            g.fillStyle = this.rgba( wd, 1 );
            shape( ax, ay, w, h, rad ); g.fill();
            // the velvet panel in the shade, a breath of light near its top, its piping
            const inset = Math.max( 0.6, w * 0.08 );
            const pg = g.createLinearGradient( 0, ay, 0, ay + h );
            pg.addColorStop( 0, this.rgba( velHi, 1 ) );
            pg.addColorStop( 0.3, this.rgba( vel, 1 ) );
            pg.addColorStop( 1, this.rgba( velLo, 1 ) );
            g.fillStyle = pg;
            shape( ax + inset, ay + inset * 1.6, w - inset * 2, h - inset * 1.6, rad * 0.75 ); g.fill();
            if ( k > 14 ) {
              g.strokeStyle = this.rgba( this.mix( velLo, [ 0, 0, 0 ], 0.3 ), 0.8 );
              g.lineWidth = Math.max( 0.5, k * 0.008 );
              shape( ax + inset * 1.5, ay + inset * 2.2, w - inset * 3, h - inset * 2.6, rad * 0.6 ); g.stroke();
            }
            // the wooden cap along its top, and the stage's light along the cap's edge
            g.strokeStyle = this.rgba( cap, 1 );
            g.lineWidth = Math.max( 0.8, inset * 1.1 );
            g.beginPath(); g.moveTo( ax + inset * 0.5, ay + rad ); g.quadraticCurveTo( ax + inset * 0.5, ay + inset * 0.5, ax + rad, ay + inset * 0.5 ); g.lineTo( ax + w - rad, ay + inset * 0.5 ); g.quadraticCurveTo( ax + w - inset * 0.5, ay + inset * 0.5, ax + w - inset * 0.5, ay + rad ); g.stroke();
            g.strokeStyle = this.rgba( rimC, 0.75 * rimA );
            g.lineWidth = Math.max( 0.5, k * 0.01 );
            g.beginPath(); g.moveTo( ax + rad * 0.7, ay + 0.4 ); g.lineTo( ax + w - rad * 0.7, ay + 0.4 ); g.stroke();
            // (kaizen) the shoulder toward the stage's middle catching a little of its light
            const shx = x < 0 ? ax + w - 0.4 : ax + 0.4;
            g.strokeStyle = this.rgba( rimC, 0.3 * rimA );
            g.beginPath(); g.moveTo( shx, ay + rad ); g.lineTo( shx, ay + h * 0.55 ); g.stroke();
          }
          // the row's end standard at the aisle, its gilded edge in the stage's light
          if ( k >= 6 ) {
            // (kaizen 7) a panel along the row, its face to the aisle seen in perspective, its gilded top edge
            const ex = side * ( aw + 0.1 );
            poly( [ P( ex, 0.8, z - 0.15 ), P( ex, 0.8, z + 0.4 ), P( ex, 0.05, z + 0.4 ), P( ex, 0.05, z - 0.15 ) ], this.rgba( this.mix( wd, [ 6, 2, 2 ], 0.15 ), 1 ) );
            poly( [ P( ex, 0.8, z - 0.15 ), P( ex, 0.8, z + 0.4 ), P( ex, 0.74, z + 0.4 ), P( ex, 0.74, z - 0.15 ) ], this.rgba( this.mix( gold, dark, 0.6 - L * 0.3 ), 1 ) );
          }
        }
      }
    }

    // the house's air: a soft warm glow from the chandelier above the middle, a darker veil low at the
    // sides
    const glow = g.createRadialGradient( W / 2, -H * 0.15, 0, W / 2, -H * 0.15, H * 0.9 );
    glow.addColorStop( 0, 'rgba(255,220,160,0.05)' );
    glow.addColorStop( 1, 'rgba(255,220,160,0)' );
    g.fillStyle = glow;
    g.fillRect( 0, 0, W, H );
    // (owner: the round shadow on the stage removed) no dark clearing behind the words: the house is dark
    void gold;
    if ( lit ) { this.backLit = c; } else { this.back = c; }
  }

  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The five colours of the theme's circle: Vienna's velvet, rose, gold, pale gold and sage. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 106, 47, 52 ], [ 215, 153, 159 ], [ 220, 168, 38 ], [ 234, 207, 140 ], [ 152, 166, 125 ] ];
  }
}
